import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "..";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { getSearchQuery } from "../common/helpers";
import { IQueryParams } from "../common/common.types";
import { IServiceOrder } from "./service-order.types";
import { server_base_endpoints } from "../../api/server-base-endpoints";
import { isArray } from "lodash";
import {
  getFileNameFromUrl,
  uploadMultipleFile,
} from "../../components/FileUpload/utils";
import { getUniqueId } from "../../helpers";
import { IFileUpload } from "../../components/FileUpload/FileUpload.type";

//=============================== Purchase Order List ===============================
export const FETCH_SERVICE_ORDER_LIST_PROGRESS =
  "FETCH_SERVICE_ORDER_LIST_PROGRESS";
export const FETCH_SERVICE_ORDER_LIST_SUCCESS =
  "FETCH_SERVICE_ORDER_LIST_SUCCESS";
export const FETCH_SERVICE_ORDER_LIST_FAILED =
  "FETCH_SERVICE_ORDER_LIST_FAILED";

export const fetchServiceOrderListProgress = () =>
  action(FETCH_SERVICE_ORDER_LIST_PROGRESS);
export const fetchServiceOrderListSuccess = (
  list: IServiceOrder[],
  totalRecords: number,
) => action(FETCH_SERVICE_ORDER_LIST_SUCCESS, { list, totalRecords });
export const fetchServiceOrderListFailed = () =>
  action(FETCH_SERVICE_ORDER_LIST_FAILED);

export const fetchServiceOrderListAsync =
  (
    queryParams: IQueryParams,
    type: "GENERAL" | "APPROVAL",
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let searchQuery = getSearchQuery(queryParams);
      dispatch(fetchServiceOrderListProgress());
      let url = `${server_base_endpoints.service.get_service_order}${searchQuery}`;
      if (type === "APPROVAL") {
        url = `/dynamicApproval/get-approval${searchQuery}&table_name=latest_service_order`;
      }

      const res = await api.get(url);
      const data: IServiceOrder[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchServiceOrderListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchServiceOrderListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err?.response?.data?.message || "Unknown Error Occurred",
          displayAs: "snackbar",
        }),
      );
    }
  };

//=============================== Fetch Single Purchase Order ===============================
export const FETCH_SINGLE_SERVICE_ORDER_PROGRESS =
  "FETCH_SINGLE_SERVICE_ORDER_PROGRESS";
export const FETCH_SINGLE_SERVICE_ORDER_SUCCESS =
  "FETCH_SINGLE_SERVICE_ORDER_SUCCESS";
export const FETCH_SINGLE_SERVICE_ORDER_FAILED =
  "FETCH_SINGLE_SERVICE_ORDER_FAILED";

export const fetchSingleServiceOrderProgress = () =>
  action(FETCH_SINGLE_SERVICE_ORDER_PROGRESS);
export const fetchSingleServiceOrderSuccess = (data: IServiceOrder) =>
  action(FETCH_SINGLE_SERVICE_ORDER_SUCCESS, { data });
export const fetchSingleServiceOrderFailed = (errorMessage: string) =>
  action(FETCH_SINGLE_SERVICE_ORDER_FAILED, { errorMessage });

export const fetchSingleServiceOrderAsync =
  (
    uuid: string,
    billing_company_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchSingleServiceOrderProgress());
      let url = `${server_base_endpoints.service.get_service_order}?service_order_uuid=${uuid}`;
      if (billing_company_uuid) {
        url += `&billing_company_uuid=${billing_company_uuid}`;
      }
      const res = await api.get(url);
      const data = res.data.data;

      if (data.length > 0) {
        let expenseData = data[0];
        expenseData.attachments = isArray(expenseData.attachments)
          ? expenseData.attachments
              .filter((item: any) => typeof item === "string")
              .map((file: string) => {
                return {
                  key: getUniqueId(),
                  type: "SERVER",
                  file: null,
                  name: getFileNameFromUrl(file || ""),
                  path: file,
                } as IFileUpload;
              })
          : [];
        dispatch(fetchSingleServiceOrderSuccess(data[0]));
      } else {
        dispatch(
          fetchSingleServiceOrderFailed("Oops! We couldn't find any records."),
        );
      }
    } catch (err: any) {
      dispatch(fetchSingleServiceOrderFailed("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

//=============================== Upsert Single Purchase Order ===============================
export const upsertSingleServiceOrderAsync =
  (
    payload: IServiceOrder,
    onCallback: (isSuccess: boolean, grnUUID?: string) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());

      const {
        rowId,
        create_ts,
        insert_ts,
        combined_service_order_no,
        created_by_name,
        modified_by_uuid,
        requested_by_uuid,
        approval_uuid,
        is_user_approver,
        prefix,
        financial_year,
        ...restPayload
      } = payload;

      const asPayload = {
        title: payload.service_order_date,
      };
      // Corrected logic for mapping receipts
      const fileToUpload = isArray(restPayload.attachments)
        ? restPayload.attachments.filter((item) => item.type === "LOCAL")
        : [];
      if (fileToUpload.length > 0) {
        const { paths } = await uploadMultipleFile(
          fileToUpload,
          "SERVICE_ORDER",
          asPayload,
        );
        restPayload.attachments = restPayload.attachments
          .filter((item) => item.type === "SERVER" && item.path)
          .map((item) => item.path!)
          .concat(paths as string[]) as any;
      } else {
        restPayload.attachments = restPayload.attachments
          .filter((item) => item.type === "SERVER" && item.path)
          .map((item) => item.path!) as any;
      }
      const res = await api.post(
        server_base_endpoints.service.upsert_service_order,
        restPayload,
      );

      const responseData: IServiceOrder = res.data.data;

      onCallback(true, responseData.service_order_uuid as string);
      dispatch(
        showMessage({
          type: "success",
          message: "Service Order saved successfully!",
          displayAs: "snackbar",
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

//=============================== Delete Single Purchase Order ===============================
export const deleteSinglePurchaseOrderAsync =
  (
    uuid: string,
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.delete("/purchase_order/delete-purchase-order", {
        data: { purchase_order_uuid: uuid },
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Purchase Order deleted successfully!",
          displayAs: "snackbar",
        }),
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

//=============================== Clear State ===============================
export const CLEAR_SINGLE_SERVICE_ORDER = "CLEAR_SINGLE_SERVICE_ORDER";
export const CLEAR_SERVICE_ORDER_STATE = "CLEAR_SERVICE_ORDER_STATE";
export const clearServiceOrderSync = () => action(CLEAR_SINGLE_SERVICE_ORDER);
export const clearServiceOrderStateSync = () =>
  action(CLEAR_SERVICE_ORDER_STATE);

// //#######################################################################################
// //############################ Purchase Order Analytics #################################
// //#######################################################################################
// export const FETCH_SERVICE_ORDER_ANALYTICS_LIST_PROGRESS = "FETCH_SERVICE_ORDER_ANALYTICS_LIST_PROGRESS";
// export const FETCH_SERVICE_ORDER_ANALYTICS_LIST_SUCCESS = "FETCH_SERVICE_ORDER_ANALYTICS_LIST_SUCCESS";
// export const FETCH_SERVICE_ORDER_ANALYTICS_LIST_FAILED = "FETCH_SERVICE_ORDER_ANALYTICS_LIST_FAILED";

// export const fetchServiceOrderAnalyticsListProgress = () =>
//   action(FETCH_SERVICE_ORDER_ANALYTICS_LIST_PROGRESS);
// export const fetchServiceOrderAnalyticsListSuccess = (data: IAnalytics[]) =>
//   action(FETCH_SERVICE_ORDER_ANALYTICS_LIST_SUCCESS, { data });
// export const fetchServiceOrderAnalyticsListFailed = () =>
//   action(FETCH_SERVICE_ORDER_ANALYTICS_LIST_FAILED);

// export const fetchServiceOrderAnalyticsWithParamsAsync =(queryParams: {
//     from_data: string;
//     to_date: string;
//   }): ThunkAction<void, IStoreState, {}, AnyAction> =>
//     async (dispatch, getState) => {
//       try {
//         dispatch(fetchServiceOrderAnalyticsListProgress());
//         let finalUrl = `/analytics/get-purchase-order-analytics?from_date=${queryParams.from_data}&to_date=${queryParams.to_date}`;
//         const res = await api.get(finalUrl);
//         const data: IAnalytics[] = res.data.data;
//         dispatch(fetchServiceOrderAnalyticsListSuccess(data));
//       } catch (err: any) {
//         dispatch(fetchServiceOrderAnalyticsListFailed());
//         dispatch(
//           showMessage({
//             type: "error",
//             message: err.response.data.message,
//             displayAs: "snackbar",
//           }),
//         );
//       }
//     };

// export const CLEAR_SERVICE_ORDER_ANALYTICS = "CLEAR_SERVICE_ORDER_ANALYTICS";

// export const clearPurchaseOrderAnalyticsSync = () => action(CLEAR_SERVICE_ORDER_ANALYTICS);

// ##########################################################################################
// ############################ Approval List#################################
// ##########################################################################################

export const FETCH_SERVICE_ORDER_APPROVAL_LIST_PROGRESS =
  "FETCH_SERVICE_ORDER_APPROVAL_LIST_PROGRESS";
export const FETCH_SERVICE_ORDER_APPROVAL_LIST_SUCCESS =
  "FETCH_SERVICE_ORDER_APPROVAL_LIST_SUCCESS";
export const FETCH_SERVICE_ORDER_APPROVAL_LIST_FAILED =
  "FETCH_SERVICE_ORDER_APPROVAL_LIST_FAILED";

export const fetchServiceOrderApprovalListProgress = () =>
  action(FETCH_SERVICE_ORDER_APPROVAL_LIST_PROGRESS);
export const fetchServiceOrderApprovalListSuccess = (
  data: IServiceOrder[],
  totalRecords: number,
) =>
  action(FETCH_SERVICE_ORDER_APPROVAL_LIST_SUCCESS, {
    data,
    totalRecords,
  });
export const fetchServiceOrderApprovalListFailed = () =>
  action(FETCH_SERVICE_ORDER_APPROVAL_LIST_FAILED);

export const fetchServiceOrderApprovalListAsync =
  (
    queryParams: IQueryParams,
    table_name: string,
    billing_company_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchServiceOrderApprovalListProgress());
      let searchQuery = getSearchQuery(queryParams);
      if (billing_company_uuid) {
        searchQuery += `&billing_company_uuid=${billing_company_uuid}`;
      }
      let finalUrl = `/approval/get-approval${searchQuery}&table_name=${table_name}`;
      const res = await api.get(finalUrl);
      const data: IServiceOrder[] = res.data.data;
      dispatch(
        fetchServiceOrderApprovalListSuccess(data, res.data.totalRecords),
      );
    } catch (err: any) {
      dispatch(fetchServiceOrderApprovalListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };
